// apps/web/src/components/CookieBanner.tsx

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const COOKIE_CONSENT_KEY = 'kc_cookie_consent';

type ConsentValue = 'all' | 'essential' | null;

const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentValue;
    if (!stored) {
      // Slight delay so it doesn't flash immediately on load
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = (value: 'all' | 'essential') => {
    setAnimateOut(true);
    setTimeout(() => {
      localStorage.setItem(COOKIE_CONSENT_KEY, value);
      setVisible(false);
    }, 350);
  };

  if (!visible) return null;

  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 right-4 md:right-auto md:left-6 md:max-w-xl z-[70] transition-all duration-300',
        animateOut ? 'translate-y-8 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      )}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="w-full rounded-2xl border border-border bg-background/95 shadow-2xl backdrop-blur-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Icon */}
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-primary"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <circle cx="8.5" cy="10.5" r="1.5" />
            <circle cx="15" cy="8" r="1" />
            <circle cx="14" cy="14.5" r="1.5" />
            <circle cx="9.5" cy="15.5" r="1" />
          </svg>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground mb-0.5">
            We use cookies
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            We use cookies to improve your experience, remember your preferences, and analyse traffic.
            By clicking "Accept All" you consent to all cookies. See our{' '}
            <Link
              to="/privacy-policy"
              className="underline underline-offset-2 hover:text-primary transition-colors"
            >
              Privacy Policy
            </Link>
            {' '}for details.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8 px-3 rounded-lg whitespace-nowrap"
            onClick={() => dismiss('essential')}
          >
            Essential only
          </Button>
          <Button
            size="sm"
            className="text-xs h-8 px-4 rounded-lg whitespace-nowrap"
            onClick={() => dismiss('all')}
          >
            Accept all
          </Button>
          <button
            onClick={() => dismiss('essential')}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close cookie banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
